import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Buffer } from "buffer";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { InputField } from "src/shared_components/FormElements";
import ChipInputField from "src/shared_components/FormElements/ChipInputField";
import EmailEditor from "./EmailEditor";
import CustomLoader from "src/shared_components/CustomLoader";
import { useSnackbar } from "src/context/ShowMessage";
import { useTranslation } from "react-i18next";
import { sendEmail } from "src/apis/ContractApis";
import { getAllClienttemplates } from "src/apis/ClientAPI";

interface Doc {
  clientId: number;
  fieldId: number;
  objectTypeId: number;
  fileId: number;
  directory: string;
  fileName: string;
  isDefault: boolean;
  viewName: string;
  contentType: string;
  fileByte: string;
  url: string | null;
  isImage: boolean;
  file: any | null;
  type: string;
  key?: string;
  variableKey?: string;
}

interface DocType {
  label: string;
  type: string;
  fetch?: (id: number) => Promise<any>;
  previewFetch?: (doc: Doc) => Promise<any>;
  fileFetch?: (doc: Doc) => Promise<any>;
  locationId?: number;
  templateReferenceId?: string;
}

interface TemplateItem {
  templateId?: number;
  templateName?: string;
  filePath?: string;
  path?: string;
  name?: string;
}

interface EmailDialogProps {
  open: boolean;
  onClose: any;
  fetchData?: any;
  sendFunction?: any;
  title: string;
  id?: number;
  docTypes?: any;
}

const schema = yup.object().shape({
  fromName: yup.string().required("From Name is required"),
  subject: yup.string().required("Subject is required"),
  to: yup
    .array()
    .of(yup.string().email("Invalid email format"))
    .min(1, "At least one recipient is required")
    .required("To field is required"),
  cc: yup.array().of(yup.string().email("Invalid email format")).nullable(),
});

const EmailDialog: React.FC<EmailDialogProps> = ({
  open,
  onClose,
  fetchData,
  title,
  sendFunction,
  id = 0,
  docTypes,
}) => {
  const { t } = useTranslation();
  const { displayMessage } = useSnackbar();
  const [template, setTemplate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [docLoading, setDocLoading] = useState<boolean>(false);
  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
  const [availableDocs, setAvailableDocs] = useState<Doc[]>([]);
  const [selectedInsuranceDocs, setSelectedInsuranceDocs] = useState<string[]>(
    [],
  );
  const [selectedOtherDocs, setSelectedOtherDocs] = useState<string[]>([]);
  const [draftSelectedOtherDocs, setDraftSelectedOtherDocs] = useState<
    string[]
  >([]);
  const [otherDocumentsOpen, setOtherDocumentsOpen] = useState<boolean>(false);
  const [otherPreviewDoc, setOtherPreviewDoc] = useState<Doc | null>(null);
  const [otherPreviewUrl, setOtherPreviewUrl] = useState<string | null>(null);
  const [otherPreviewOpen, setOtherPreviewOpen] = useState(false);
  const [selectedTemplatePaths, setSelectedTemplatePaths] = useState<
    Record<string, string | null>
  >({});
  const [templateOptions, setTemplateOptions] = useState<TemplateItem[]>([]);
  const [templateType, setTemplateType] = useState<string | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);

  const otherDocType = docTypes?.find((doc: DocType) => doc.label === "Other");
  const otherAttachmentType = otherDocType?.type;
  const isOtherType = (type: string) => type === otherAttachmentType;

  const addSelectedTemplateDocument = (
    type: string,
    path: string | null,
    name?: string,
  ) => {
    const docType = docTypes?.find((doc: DocType) => doc.type === type);
    setAvailableDocs((prev) => [
      ...prev.filter((doc) => doc.type !== type),
      {
        clientId: 0,
        fieldId: id,
        objectTypeId: 0,
        fileId: 0,
        directory: path || "",
        fileName: name || docType?.label || type,
        isDefault: false,
        viewName: docType?.label || type,
        contentType: "application/pdf",
        fileByte: "",
        url: null,
        isImage: false,
        file: null,
        type,
      },
    ]);
  };

  const methods = useForm<any>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      fromName: "",
      subject: "",
      to: [],
      cc: [],
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (open && fetchData) {
      const fetchEmailTemplate = async () => {
        setLoading(true);
        try {
          const response = await fetchData();
          if (response?.status === 200 && response?.data) {
            reset(response.data);
            setTemplate(response.data.emailBody);
          } else {
            displayMessage(
              response?.data || "Failed to fetch email content",
              "error",
            );
          }
        } catch (error: any) {
          displayMessage(error.message, "error");
        } finally {
          setLoading(false);
        }
      };
      fetchEmailTemplate();
    }
  }, [open, fetchData, reset, displayMessage]);

  const openOtherDocumentsSelector = async () => {
    setDraftSelectedOtherDocs(selectedOtherDocs);
    setOtherDocumentsOpen(true);

    const docFetch = otherDocType?.fetch;
    if (!docFetch) return;

    setDocLoading(true);
    try {
      const response = await docFetch(id);
      if (response?.status === 200 || response?.status === 404) {
        const documents = Array.isArray(response?.data) ? response.data : [];
        const newDocs = documents.map((doc: Doc) => ({
          ...doc,
          type: otherAttachmentType,
        }));
        setAvailableDocs((prev) => [
          ...prev.filter((doc) => !isOtherType(doc.type)),
          ...newDocs,
        ]);
      } else {
        displayMessage(
          response?.data || "Failed to fetch Other documents",
          "error",
        );
      }
    } catch (error: any) {
      displayMessage(error.message, "error");
    } finally {
      setDocLoading(false);
    }
  };

  const handleDocCheckboxChange = async (type: string, checked: boolean) => {
    if (isOtherType(type)) {
      if (checked) {
        await openOtherDocumentsSelector();
      } else {
        setSelectedDocTypes((prev) => prev.filter((doc) => !isOtherType(doc)));
        setSelectedOtherDocs([]);
        setDraftSelectedOtherDocs([]);
        setOtherDocumentsOpen(false);
      }
      return;
    }

    const selectedDocType = docTypes?.find(
      (doc: DocType) => doc.type === type,
    );
    if (selectedDocType?.templateReferenceId) {
      if (!checked) {
        setSelectedDocTypes((prev) => prev.filter((doc) => doc !== type));
        setAvailableDocs((prev) => prev.filter((doc) => doc.type !== type));
        setSelectedTemplatePaths((prev) => {
          const updated = { ...prev };
          delete updated[type];
          return updated;
        });
        return;
      }

      setTemplateLoading(true);
      try {
        const response = await getAllClienttemplates(
          selectedDocType.locationId,
          selectedDocType.templateReferenceId,
          { isEmailable: true },
        );
        const templates = Array.isArray(response?.data) ? response.data : [];

        if (response?.status !== 200) {
          displayMessage(
            response?.data || "Failed to fetch PDF templates",
            "error",
          );
          return;
        }

        if (templates.length === 0) {
          setSelectedTemplatePaths((prev) => ({ ...prev, [type]: null }));
          addSelectedTemplateDocument(type, null, selectedDocType.label);
          setSelectedDocTypes((prev) =>
            prev.includes(type) ? prev : [...prev, type],
          );
        } else if (templates.length === 1) {
          const path = templates[0]?.filePath || templates[0]?.path || null;
          setSelectedTemplatePaths((prev) => ({ ...prev, [type]: path }));
          addSelectedTemplateDocument(
            type,
            path,
            templates[0]?.templateName || templates[0]?.name,
          );
          setSelectedDocTypes((prev) =>
            prev.includes(type) ? prev : [...prev, type],
          );
        } else {
          setTemplateOptions(templates);
          setTemplateType(type);
          setTemplateDialogOpen(true);
        }
      } catch (error: any) {
        displayMessage(error.message || "Failed to fetch PDF templates", "error");
      } finally {
        setTemplateLoading(false);
      }
      return;
    }

    setSelectedDocTypes((prev) =>
      checked ? [...prev, type] : prev.filter((d) => d !== type),
    );

    if (checked) {
      const docFetch = docTypes?.find((d: any): any => d.type === type)?.fetch;
      if (docFetch) {
        setDocLoading(true);
        try {
          const res = await docFetch(id);
          if (res?.status === 200 && res?.data) {
            const newDocs = res.data.map((doc: Doc) => ({
              ...doc,
              type,
            }));
            setAvailableDocs((prev) => [
              ...prev.filter((d) => d.type !== type),
              ...newDocs,
            ]);
            // if (type === "VehicleInsurance") {
            //   setSelectedInsuranceDocs(
            //     newDocs.map((doc: Doc) => doc.directory)
            //   );
            // }
          } else {
            displayMessage(
              res?.data || `Failed to fetch ${type} documents`,
              "error",
            );
          }
        } catch (error: any) {
          displayMessage(error.message, "error");
        } finally {
          setDocLoading(false);
        }
      } else {
        setAvailableDocs((prev) => [
          ...prev.filter((d) => d.type !== type),
          {
            clientId: 0,
            fieldId: id,
            objectTypeId: 0,
            fileId: 0,
            directory: type,
            fileName: type,
            isDefault: false,
            viewName: type,
            contentType: "",
            fileByte: "",
            url: null,
            isImage: false,
            file: null,
            type,
          },
        ]);
      }
    } else {
      setAvailableDocs((prev) => prev.filter((d) => d.type !== type));
      if (type === "VehicleInsurance") {
        setSelectedInsuranceDocs([]);
      }
    }
  };

  const handleInsuranceDocChange = (docId: string, checked: boolean) => {
    setSelectedInsuranceDocs((prev) =>
      checked ? [...prev, docId] : prev.filter((id: any) => id !== docId),
    );
  };

  const getDocumentKey = (doc: Doc) =>
    doc.variableKey || doc.key || doc.directory;

  const handleOtherDocChange = (documentKey: string, checked: boolean) => {
    setDraftSelectedOtherDocs((prev) =>
      checked
        ? [...prev, documentKey]
        : prev.filter((key) => key !== documentKey),
    );
  };

  const cancelOtherDocumentSelection = () => {
    setDraftSelectedOtherDocs(selectedOtherDocs);
    setOtherDocumentsOpen(false);
  };

  const confirmOtherDocumentSelection = () => {
    setSelectedOtherDocs(draftSelectedOtherDocs);
    setSelectedDocTypes((prev) =>
      otherAttachmentType && !prev.includes(otherAttachmentType)
        ? [...prev, otherAttachmentType]
        : prev,
    );
    setOtherDocumentsOpen(false);
  };

  const removeSelectedOtherDocument = (documentKey: string) => {
    setSelectedOtherDocs((prev) => {
      const updated = prev.filter((key) => key !== documentKey);
      if (updated.length === 0) {
        setSelectedDocTypes((types) =>
          types.filter((type) => !isOtherType(type)),
        );
      }
      return updated;
    });
  };

  const getDocumentContentType = (doc: Doc) => {
    const contentType = (doc.contentType || "").toLowerCase();
    const fileName = (doc.fileName || "").toLowerCase();

    if (contentType === "application/pdf" || fileName.endsWith(".pdf")) {
      return "application/pdf";
    }
    if (contentType.startsWith("image/")) return contentType;
    if (/\.(png|jpe?g|gif|bmp|webp|svg)$/.test(fileName)) {
      const extension = fileName.split(".").pop();
      return extension === "jpg" ? "image/jpeg" : `image/${extension || "png"}`;
    }
    if (contentType.startsWith("text/") || fileName.endsWith(".txt")) {
      return contentType.startsWith("text/") ? contentType : "text/plain";
    }
    return contentType || "application/octet-stream";
  };

  const canPreviewOtherDocument = (doc: Doc) => {
    const contentType = getDocumentContentType(doc);
    return (
      contentType === "application/pdf" ||
      contentType.startsWith("image/") ||
      contentType.startsWith("text/")
    );
  };

  const handleOtherPreview = async (doc: Doc) => {
    if (!otherDocType?.fileFetch) return;

    try {
      const response = await otherDocType.fileFetch(doc);
      if (response?.status !== 200 || !response?.data) {
        throw new Error("Document content was not returned");
      }

      const binaryBuffer: any = Buffer.from(response.data, "base64");
      const blob = new Blob([binaryBuffer], {
        type: getDocumentContentType(doc),
      });
      if (otherPreviewUrl) URL.revokeObjectURL(otherPreviewUrl);
      setOtherPreviewDoc(doc);
      setOtherPreviewUrl(URL.createObjectURL(blob));
      setOtherPreviewOpen(true);
    } catch (error: any) {
      displayMessage(error.message || "Preview failed", "error");
    }
  };

  const closeOtherPreview = () => {
    setOtherPreviewOpen(false);
    if (otherPreviewUrl) URL.revokeObjectURL(otherPreviewUrl);
    setOtherPreviewDoc(null);
    setOtherPreviewUrl(null);
  };

  const selectPdfTemplate = (template: TemplateItem) => {
    if (!templateType) return;
    const path = template.filePath || template.path || null;
    setSelectedTemplatePaths((prev) => ({
      ...prev,
      [templateType]: path,
    }));
    addSelectedTemplateDocument(
      templateType,
      path,
      template.templateName || template.name,
    );
    setSelectedDocTypes((prev) =>
      prev.includes(templateType) ? prev : [...prev, templateType],
    );
    setTemplateDialogOpen(false);
    setTemplateOptions([]);
    setTemplateType(null);
  };

  const closeTemplateDialog = () => {
    setTemplateDialogOpen(false);
    setTemplateOptions([]);
    setTemplateType(null);
  };

  const handlePreview = async (doc: Doc) => {
    const docType = docTypes?.find((d: any) => d.type === doc.type);
    if (docType?.previewFetch) {
      try {
        await docType.previewFetch(doc);
      } catch (error: any) {
        displayMessage(error.message, "error");
      }
    }
  };

  const onSubmit = async (values: any) => {
    setLoading(true);
    const details = [
      ...selectedDocTypes
        .filter((type) => type !== "VehicleInsurance" && !isOtherType(type))
        .map((type) => {
          const path = selectedTemplatePaths[type] ?? null;
          return {
            attachmentType: type,
            key: path,
            path,
          };
        }),

      ...selectedInsuranceDocs.map((docId) => ({
        attachmentType: "VehicleInsurance",
        key: docId,
        path: docId,
      })),

      ...selectedOtherDocs.map((documentKey) => ({
        attachmentType: otherAttachmentType,
        key: documentKey,
        path: documentKey,
      })),
    ];

    try {
      const body = {
        ...values,
        emailBody: template,
        fieldId: id,
        attachmentsDetails: details,
      };
      const response = sendFunction
        ? await sendFunction(id, body)
        : await sendEmail(id, body);

      if (response?.status === 200) {
        displayMessage("Email sent successfully", "success");
        onClose();
      } else {
        displayMessage(response?.data || "Failed to send email", "error");
      }
    } catch (error: any) {
      displayMessage(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const emailSchema = yup
    .string()
    .email("Invalid email format")
    .required("Email is required");

  const selectedAttachmentCount =
    selectedDocTypes.filter(
      (type) => type !== "VehicleInsurance" && !isOtherType(type),
    ).length +
    selectedInsuranceDocs.length +
    selectedOtherDocs.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ style: { width: "80%", borderRadius: 8 } }}
    >
      <DialogTitle sx={{ fontWeight: "bold", bgcolor: "#f5f5f5", py: 2 }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <CustomLoader />
        ) : (
          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} md={6}>
              <InputField
                fieldName="fromName"
                control={control}
                label="From *"
                error={!!errors?.fromName}
                helperText={errors?.fromName?.message}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputField
                fieldName="subject"
                control={control}
                label="Subject *"
                error={!!errors?.subject}
                helperText={errors?.subject?.message}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipInputField
                fieldName="to"
                control={control}
                label="To *"
                error={!!errors?.to}
                helperText={errors?.to?.message}
                setValue={setValue}
                schema={emailSchema}
                setError={setError}
                clearError={clearErrors}
                placeholder="Enter TO emails ..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipInputField
                fieldName="cc"
                control={control}
                label="CC"
                error={!!errors?.cc}
                helperText={errors?.cc?.message}
                setValue={setValue}
                schema={emailSchema}
                setError={setError}
                clearError={clearErrors}
                placeholder="Enter CC emails ..."
              />
            </Grid>

            <Grid item xs={12}>
              <EmailEditor template={template} setTemplate={setTemplate} />
            </Grid>
            {docTypes?.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Select Documents to Attach
                </Typography>
                <Grid container spacing={2}>
                  {docTypes?.map((doc: any) => (
                    <Grid item key={doc.type}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedDocTypes.includes(doc.type)}
                            onChange={(e) =>
                              handleDocCheckboxChange(
                                doc.type,
                                e.target.checked,
                              )
                            }
                            color="primary"
                          />
                        }
                        label={doc.label}
                        sx={{ mr: 2 }}
                      />
                    </Grid>
                  ))}
                </Grid>
                {docLoading && (
                  <CircularProgress size={20} sx={{ mt: 1, ml: 2 }} />
                )}
                {templateLoading && (
                  <CircularProgress size={20} sx={{ mt: 1, ml: 2 }} />
                )}
              </Grid>
            )}
            {docTypes?.length > 0 && (
              <Grid item xs={12}>
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { m: 0 },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Available Documents
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedAttachmentCount > 0
                          ? `${selectedAttachmentCount} document${
                              selectedAttachmentCount === 1 ? "" : "s"
                            } chosen`
                          : "No documents chosen"}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {otherAttachmentType && selectedOtherDocs.length === 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mb: 1,
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={openOtherDocumentsSelector}
                        >
                          Choose Other Documents
                        </Button>
                      </Box>
                    )}

                    {availableDocs.some((doc) => !isOtherType(doc.type)) && (
                      <List disablePadding>
                        {availableDocs
                          .filter((doc) => !isOtherType(doc.type))
                          .map((doc) => (
                            <React.Fragment key={`${doc.type}-${doc.fileId}`}>
                              <ListItem>
                                {doc.viewName === "Insurance Policy" ? (
                                  doc.contentType === "application/pdf" && (
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={selectedInsuranceDocs.includes(
                                            doc.directory,
                                          )}
                                          onChange={(event) =>
                                            handleInsuranceDocChange(
                                              doc.directory,
                                              event.target.checked,
                                            )
                                          }
                                          color="primary"
                                        />
                                      }
                                      label={
                                        <ListItemText
                                          primary={doc.fileName}
                                          secondary={`Type: ${doc.viewName}`}
                                        />
                                      }
                                    />
                                  )
                                ) : (
                                  <ListItemText
                                    primary={doc.fileName}
                                    secondary={`Type: ${doc.viewName}`}
                                  />
                                )}
                                <ListItemSecondaryAction>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handlePreview(doc)}
                                    disabled={
                                      !docTypes?.find(
                                        (item: any) => item.type === doc.type,
                                      )?.previewFetch
                                    }
                                  >
                                    Preview
                                  </Button>
                                </ListItemSecondaryAction>
                              </ListItem>
                              <Divider />
                            </React.Fragment>
                          ))}
                      </List>
                    )}

                    {selectedOtherDocs.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={2}
                          sx={{ px: 2, py: 1 }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold">
                            Chosen Other Documents
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={openOtherDocumentsSelector}
                          >
                            Adjust Other Documents ({selectedOtherDocs.length})
                          </Button>
                        </Stack>
                        <List disablePadding>
                          {availableDocs
                            .filter(
                              (doc) =>
                                isOtherType(doc.type) &&
                                selectedOtherDocs.includes(getDocumentKey(doc)),
                            )
                            .map((doc) => (
                              <ListItem key={`chosen-${doc.fileId}`} divider>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked
                                      onChange={() =>
                                        removeSelectedOtherDocument(
                                          getDocumentKey(doc),
                                        )
                                      }
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <ListItemText
                                      primary={doc.fileName}
                                      secondary={
                                        getDocumentContentType(doc) ===
                                        "application/pdf"
                                          ? "PDF"
                                          : getDocumentContentType(
                                                doc,
                                              ).startsWith("image/")
                                            ? "Image"
                                            : "File"
                                      }
                                    />
                                  }
                                />
                                <ListItemSecondaryAction>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleOtherPreview(doc)}
                                    disabled={
                                      !otherDocType?.fileFetch ||
                                      !canPreviewOtherDocument(doc)
                                    }
                                  >
                                    Preview
                                  </Button>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                        </List>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}

            {(availableDocs.some((doc) => !isOtherType(doc.type)) ||
              selectedOtherDocs.length > 0) && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Documents attached in email:</strong>{" "}
                  {availableDocs
                    .filter(
                      (doc) =>
                        (!isOtherType(doc.type) &&
                          doc.viewName !== "Insurance Policy") ||
                        selectedInsuranceDocs.includes(doc.directory) ||
                        selectedOtherDocs.includes(getDocumentKey(doc)),
                    )
                    .map((doc) => doc.fileName)
                    .join(", ")}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, bgcolor: "#f5f5f5" }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={loading}
          sx={{ mr: 1 }}
        >
          {t("common.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {t("common.buttons.confirm")}
        </Button>
      </DialogActions>

      <Dialog
        open={otherDocumentsOpen}
        onClose={cancelOtherDocumentSelection}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Other Documents</DialogTitle>
        <DialogContent dividers>
          {docLoading ? (
            <Grid container justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Grid>
          ) : availableDocs.filter((doc) => isOtherType(doc.type)).length >
            0 ? (
            <List disablePadding>
              {availableDocs
                .filter((doc) => isOtherType(doc.type))
                .map((doc) => (
                  <ListItem key={doc.fileId} divider>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={draftSelectedOtherDocs.includes(
                            getDocumentKey(doc),
                          )}
                          onChange={(event) =>
                            handleOtherDocChange(
                              getDocumentKey(doc),
                              event.target.checked,
                            )
                          }
                          color="primary"
                        />
                      }
                      label={
                        <ListItemText
                          primary={doc.fileName}
                          secondary={
                            getDocumentContentType(doc) === "application/pdf"
                              ? "PDF"
                              : getDocumentContentType(doc).startsWith("image/")
                                ? "Image"
                                : getDocumentContentType(doc).startsWith(
                                      "text/",
                                    )
                                  ? "Text document"
                                  : "File"
                          }
                        />
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOtherPreview(doc)}
                        disabled={
                          !otherDocType?.fileFetch ||
                          !canPreviewOtherDocument(doc)
                        }
                      >
                        Preview
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
            </List>
          ) : (
            <Typography color="text.secondary" py={3} textAlign="center">
              No documents found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {availableDocs.some((doc) => isOtherType(doc.type)) ? (
            <>
              <Button onClick={cancelOtherDocumentSelection}>Cancel</Button>
              <Button
                variant="contained"
                onClick={confirmOtherDocumentSelection}
                disabled={draftSelectedOtherDocs.length === 0}
              >
                Add Selected ({draftSelectedOtherDocs.length})
              </Button>
            </>
          ) : (
            <Button onClick={cancelOtherDocumentSelection}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={otherPreviewOpen}
        onClose={closeOtherPreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {otherPreviewDoc?.fileName || "Document Preview"}
        </DialogTitle>
        <DialogContent dividers sx={{ height: "70vh" }}>
          {otherPreviewDoc && otherPreviewUrl && (
            <>
              {getDocumentContentType(otherPreviewDoc) === "application/pdf" ||
              getDocumentContentType(otherPreviewDoc).startsWith("text/") ? (
                <iframe
                  src={otherPreviewUrl}
                  title={otherPreviewDoc.fileName}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                />
              ) : (
                <Box
                  component="img"
                  src={otherPreviewUrl}
                  alt={otherPreviewDoc.fileName}
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOtherPreview}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={templateDialogOpen}
        onClose={closeTemplateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Template</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the PDF template to attach to this email.
          </Typography>
          <List disablePadding>
            {templateOptions.map((template, index) => {
              const path = template.filePath || template.path;
              return (
                <React.Fragment
                  key={template.templateId ?? path ?? index}
                >
                  <ListItemButton onClick={() => selectPdfTemplate(template)}>
                    <ListItemText
                      primary={
                        template.templateName || template.name || "Template"
                      }
                    />
                  </ListItemButton>
                  {index < templateOptions.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTemplateDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EmailDialog;
